import React, {useState} from "react";
import {Box, Button, MenuItem, TextField, Typography} from "@mui/material";
import AuthShell, {BLUE, ErrorText, MUTED} from "../components/AuthShell";
import {changeInitialPassword, selectAccount, signIn} from "../util/api";
import {PortalAccount, establishSession} from "../util/SessionContext";

// Supplier portal sign-in.
//
// Two phases, mirroring the endpoint: credentials, then -- only when the account list came back with
// more than one entry -- the picker. The picker is never reachable without a verified password, which
// is the whole reason the account list is not fetched from the email alone: it names our customers.
//
// Every credential failure renders ONE message. The server already refuses to distinguish an unknown
// address from a wrong password, and a UI that split them would undo that.

type Phase = "credentials" | "account" | "password";

// Matches the endpoint, which refuses anything shorter.
const MINIMUM_PASSWORD_LENGTH = 8;

export default function Login({onSignedIn, onForgotPassword}: {onSignedIn: () => void; onForgotPassword: () => void}) {
    const [phase, setPhase] = useState<Phase>("credentials");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [accounts, setAccounts] = useState<PortalAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string>("");
    const [selectionToken, setSelectionToken] = useState<string>("");
    const [tempToken, setTempToken] = useState<string>("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string>("");
    const [busy, setBusy] = useState(false);

    const submitCredentials = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");

        const result = await signIn(email.trim(), password);

        setBusy(false);

        if (result.status === 429) {
            setError("Too many failed attempts. Try again later, or reset your password.");
            return;
        }

        if (result.status !== 200) {
            setError("That email address and password do not match.");
            return;
        }

        const data = result.data ?? {};

        if (data.Code === "CC-T9PW-INIT") {
            // The row still carries the password an administrator issued. The token that came back
            // is scoped to the change-password endpoint alone, so the only way on is to set one.
            setTempToken(data.AccessToken ?? "");
            setPhase("password");
            return;
        }

        if (data.Accounts && data.SelectionToken) {
            setAccounts(data.Accounts);
            setSelectionToken(data.SelectionToken);

            // One account is not a choice. The server already answers a single membership with a
            // session and no token at all, so this only fires if that ever changes -- but a picker
            // offering exactly one option is a dead click either way, so spend the token here.
            if (data.Accounts.length === 1) {
                await choose(data.Accounts[0], data.SelectionToken);
                return;
            }

            setSelectedAccount(data.Accounts[0].AccountUSID);
            setPhase("account");
            return;
        }

        if (data.AccessToken) {
            establishSession(data);
            onSignedIn();
            return;
        }

        setError("Something went wrong signing you in. Please try again.");
    };

    // The token is passed in on the auto-select path: setSelectionToken has not landed in state yet
    // when the credentials step decides there is only one account to spend it on.
    const choose = async (account: PortalAccount, token?: string) => {
        if (busy) return;
        setBusy(true);
        setError("");

        const result = await selectAccount(token ?? selectionToken, account.AccountUSID);

        setBusy(false);

        if (result.status !== 200 || !result.data?.AccessToken) {
            // The selection token lives five minutes; expiry is the likeliest cause here.
            setError("That sign-in attempt expired. Please enter your details again.");
            setPhase("credentials");
            setPassword("");
            return;
        }

        // Several accounts means the forced change arrives HERE rather than at the credentials step,
        // because the account has to be chosen before a session -- or a temp token -- can name one.
        if (result.data.Code === "CC-T9PW-INIT") {
            setTempToken(result.data.AccessToken);
            setPhase("password");
            return;
        }

        establishSession(result.data);
        onSignedIn();
    };

    if (phase === "password") {
        const submitNewPassword = async (event: React.FormEvent) => {
            event.preventDefault();
            if (busy) return;

            if (newPassword !== confirmPassword) {
                setError("Those passwords do not match.");
                return;
            }

            if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
                setError(`Your password must be at least ${MINIMUM_PASSWORD_LENGTH} characters.`);
                return;
            }

            setBusy(true);
            setError("");

            const result = await changeInitialPassword(tempToken, newPassword);

            setBusy(false);

            if (result.status === 200 && result.data?.AccessToken) {
                // The endpoint returns a full session, so there is nothing to sign in to again.
                establishSession(result.data);
                onSignedIn();
                return;
            }

            if (result.data?.Code === "CC-T9CP-SAME") {
                setError("Choose a password different from the one you were given.");
                return;
            }

            // The temp token lives thirty minutes; expiry is the likeliest cause of anything else.
            setError("That sign-in attempt expired. Please enter your details again.");
            setPhase("credentials");
            setPassword("");
            setNewPassword("");
            setConfirmPassword("");
        };

        return (
            <AuthShell title="Choose a password"
                       subtitle="You signed in with a password that was issued for you. Set your own to continue.">
                <form onSubmit={submitNewPassword}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField label="New password" type="password" size="small" autoComplete="new-password"
                                   value={newPassword} onChange={e => setNewPassword(e.target.value)} required fullWidth/>
                        <TextField label="Confirm new password" type="password" size="small" autoComplete="new-password"
                                   value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required fullWidth/>

                        {error && <ErrorText>{error}</ErrorText>}

                        <Button type="submit" variant="contained" disableElevation disabled={busy}
                                sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                            {busy ? "Saving\u2026" : "Save password"}
                        </Button>
                    </Box>
                </form>
            </AuthShell>
        );
    }

    if (phase === "account") {
        const submitAccount = (event: React.FormEvent) => {
            event.preventDefault();
            const account = accounts.find(a => a.AccountUSID === selectedAccount);
            if (account) void choose(account);
        };

        return (
            <AuthShell title="Choose an account" subtitle="Your sign-in is linked to more than one account.">
                <form onSubmit={submitAccount}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField select label="Account" size="small" fullWidth disabled={busy}
                                   value={selectedAccount}
                                   onChange={e => setSelectedAccount(e.target.value)}>
                            {accounts.map(account => (
                                <MenuItem key={account.AccountUSID} value={account.AccountUSID}>
                                    {account.AccountName}
                                </MenuItem>
                            ))}
                        </TextField>

                        {error && <ErrorText>{error}</ErrorText>}

                        {/* Selecting is not signing in: the choice is confirmed here, so a mis-click in
                            the list costs nothing. */}
                        <Button type="submit" variant="contained" disableElevation disabled={busy || !selectedAccount}
                                sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                            {busy ? "Signing in…" : "Continue"}
                        </Button>
                    </Box>
                </form>

                <Typography onClick={() => { setPhase("credentials"); setPassword(""); setError(""); }}
                            sx={{fontSize: 13, color: BLUE, cursor: "pointer", textAlign: "center"}}>
                    Back
                </Typography>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="Sign in">
            <form onSubmit={submitCredentials}>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <TextField label="Email" type="email" size="small" autoComplete="username"
                               value={email} onChange={e => setEmail(e.target.value)} required fullWidth/>
                    <TextField label="Password" type="password" size="small" autoComplete="current-password"
                               value={password} onChange={e => setPassword(e.target.value)} required fullWidth/>

                    {error && <ErrorText>{error}</ErrorText>}

                    <Button type="submit" variant="contained" disableElevation disabled={busy}
                            sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                        {busy ? "Signing in\u2026" : "Sign in"}
                    </Button>
                </Box>
            </form>

            <Typography onClick={onForgotPassword}
                        sx={{fontSize: 13, color: BLUE, cursor: "pointer", textAlign: "center"}}>
                Forgot password
            </Typography>

            <Typography sx={{fontSize: 12, color: MUTED, textAlign: "center"}}>
                Access is by invitation from the company you work with.
            </Typography>
        </AuthShell>
    );
}
